import { CombinedGraphQLErrors, isErrorLike } from "@apollo/client/errors";

export const REDACTED_SUBGRAPH_MESSAGE = "Subgraph errors redacted";

export interface GraphQLErrorLike {
  // skipcq: JS-T1001
  message?: string | undefined;
  // skipcq: JS-T1001
  extensions?: Readonly<Record<string, unknown>> | undefined;
}

const SPECIFIC_COPY: Readonly<Record<string, string>> = {
  ALREADY_INVITED_OR_MEMBER: "That person is already invited or a member of this organization.",
  PERSONAL_ORGANIZATION_INVITE_NOT_ALLOWED: "Personal organizations can't invite other members.",
  INVITATION_NOT_FOUND: "That invitation no longer exists. It may have been accepted or revoked.",
  USER_NOT_FOUND: "That user could not be found.",
  EMAIL_NOT_FOUND: "No user exists with that email address.",
  ACCESS_REQUEST_NOT_FOUND: "That access request could not be found.",
  ACCESS_REQUEST_ALREADY_RESOLVED: "That access request has already been resolved.",
  USER_ALREADY_EXISTS: "A user with that email already exists.",
  INVALID_USER_SIGNUP: "That sign-up is invalid. Check the details and try again.",
  INVALID_ENROLLMENT_INPUT: "The enrollment details are invalid. Check the input and try again.",
};

const REDACTED_COPY: Readonly<Record<string, string>> = {
  FORBIDDEN: "You don't have permission to do that.",
  UNAUTHENTICATED: "Your session has expired. Please sign in again.",
  BAD_USER_INPUT: "The request was invalid. Check the input and try again.",
  NOT_FOUND: "The requested record could not be found.",
};

const NOT_FOUND_SUFFIX = "_NOT_FOUND";

export function codeOf(error: GraphQLErrorLike): string | undefined {
  const code = error.extensions?.code;
  return typeof code === "string" ? code : undefined;
}

function isRedacted(message: string | undefined): message is undefined | "" | typeof REDACTED_SUBGRAPH_MESSAGE {
  return !message || message === REDACTED_SUBGRAPH_MESSAGE;
}

function informativeness(graphQLError: GraphQLErrorLike): number {
  const code = codeOf(graphQLError);
  if (code && SPECIFIC_COPY[code]) return 3;
  if (!isRedacted(graphQLError.message)) return 2;
  if (code && (REDACTED_COPY[code] || code.endsWith(NOT_FOUND_SUFFIX))) return 1;
  return 0;
}

function primaryGraphQLError(error: unknown): GraphQLErrorLike | undefined {
  if (!CombinedGraphQLErrors.is(error)) return undefined;
  return error.errors.reduce<GraphQLErrorLike | undefined>(
    (best, candidate) => (best && informativeness(best) >= informativeness(candidate) ? best : candidate),
    // skipcq: JS-W1042 — the initial value is load-bearing; reduce throws on an empty array without it
    undefined
  );
}

export function graphQLErrorCode(error: unknown): string | undefined {
  const graphQLError = primaryGraphQLError(error);
  return graphQLError ? codeOf(graphQLError) : undefined;
}

export function isRedactedGraphQLError(error: unknown): boolean {
  const graphQLError = primaryGraphQLError(error);
  return graphQLError !== undefined && isRedacted(graphQLError.message);
}

function redactedCopy(code: string | undefined, service: unknown): string {
  const copy = code ? REDACTED_COPY[code] : undefined;
  if (copy) return copy;
  if (code?.endsWith(NOT_FOUND_SUFFIX)) {
    const entity = code.slice(0, -NOT_FOUND_SUFFIX.length).toLowerCase().replace(/_/g, " ");
    return `The ${entity} could not be found.`;
  }
  return typeof service === "string" && service
    ? `Something went wrong in the ${service} service. Please try again or check logs.`
    : "Something went wrong. Please try again or check logs.";
}

export function describeGraphQLError(error: unknown, fallback: string): string {
  const graphQLError = primaryGraphQLError(error);
  if (graphQLError) {
    const code = codeOf(graphQLError);
    const copy = code ? SPECIFIC_COPY[code] : undefined;
    if (copy) return copy;
    if (isRedacted(graphQLError.message)) {
      return redactedCopy(code, graphQLError.extensions?.service);
    }
    return graphQLError.message;
  }
  return isErrorLike(error) && error.message ? error.message : fallback;
}
